// Internal Imports
import { API_ENDPOINTS } from "../../constants";
import { deleteApi, get, post, put } from "../../api/index";

// Destructuring
const { APIS } = API_ENDPOINTS;

/**
 * "fetchDealTableColumns" func fetches dealtable column definitions.
 * @param {string} DealTableViewId - Refers to the page view id // "VIWxlvEP8HgeEAJ".
 */
export const fetchDealTableColumns = async DealTableViewId => {
  const columnData = await get(`${APIS.VIEW_METADATA}/${DealTableViewId}`)
    .then(res => res)
    .catch(error => {
      console.error("DealTableServices => fetchDealTableColumns", error);
      throw error;
    });
  return columnData;
};

/**
 * "fetchDealTableRows" func fetches dealtable rows.
 * @param {string} DealTableId - Refers to the dealtable id to be fetched // "15"
 * @param {string} DealTableViewId - Refers to the page view id // "VIWxlvEP8HgeEAJ".
 */
export const fetchDealTableRows = async (DealTableId, DealTableViewId) => {
  const rowData = await get(
    `${APIS.CHILD_VIEWS}/${DealTableId}/${DealTableViewId}`,
  )
    .then(res => res)
    .catch(error => {
      console.error("DealTableServices => fetchDealTableRows", error);
      throw error;
    });
  return rowData;
};

/**
 * "fetchProductCatalogs" func fetches products data based on pricelistId, search phrase &  rows.
 * @param {string} DealTableId - Refers to the dealtable id to be fetched // "15"
 * @param {string} DealTableViewId - Refers to the page view id // "VIWxlvEP8HgeEAJ".
 */
export const fetchProductCatalogs = async (
  pricelistID,
  searchQuery,
  productType,
) => {
  const productTypeParam =
    productType !== "All Product Types" ? `&product_type=${productType}` : "";
  const productCatalogs = await get(
    `${
      APIS.CATALOGS
    }/${pricelistID}/items?text=${searchQuery}${productTypeParam}`,
  )
    .then(res => res)
    .catch(error => {
      console.error("DealTableServices => fetchProductCatalogs", error);
      throw error;
    });
  return productCatalogs;
};

/**
 * "removeDealTableRow" func removes the row from the dealtable.
 * @param {string} masterTblID - Master ID for the table // "TBLmkX4dxHn548K"
 * @param {number} dealTableId - Refers to the dealtable id // 15.
 * @param {string} tableId - Refers to a sub table of the master table // "TBLgXjNRZHlVNdl".
 * @param {number} rowId - Refers to the ID of the row // 2.
 */
export const removeDealTableRow = async (
  masterTblID,
  dealTableId,
  tableId,
  rowId,
) => {
  const rowData = await deleteApi(
    `${APIS.TABLES}/${masterTblID}/${dealTableId}/${tableId}?id=${rowId}`,
  )
    .then(res => res)
    .catch(error => {
      console.error("DealTableServices => removeDealTableRow", error);
      throw error;
    });
  return rowData;
};

/**
 * "saveDealTableRow" func saves the row in the dealtable.
 * @param {number} dealTableId - Refers to the dealtable id // 15.
 * @param {arrayOfObj} filteredRows - Contains the row data to be saved.
 * @param {string} masterTblID - Master ID for the table // "TBLmkX4dxHn548K"
 * @param {string} tableId - Refers to a sub table of the master table // "TBLgXjNRZHlVNdl".
 * @param {string} tblViewId - Refers to the View ID of the table // "VIWBVM4ggH6842X".
 */
export const saveDealTableRow = async (
  dealTableId,
  filteredRows,
  masterTblID,
  tableId,
  tblViewId,
) => {
  const rowData = await put(
    `${
      APIS.TABLES
    }/${masterTblID}/${dealTableId}/${tableId}?view_id=${tblViewId}`,
    filteredRows,
  )
    .then(res => res)
    .catch(error => {
      console.error("DealTableServices => saveDealTableRow", error);
      throw error;
    });
  return rowData;
};

/**
 * "createDealTableGroup" func adds group(s) to a dealtable.
 * @param {number} dealTableId - Refers to the dealtable id // 15.
 * @param {arrayofObj} groups - Contains the JSON of group payload.
 */
export const createDealTableGroup = (dealTableId, groups) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DEALTABLES}/${dealTableId}/groups`, groups)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

/**
 * "updateDealTableGroup" func updates group(s) data to a dealtable.
 * @param {number} dealTableId - Refers to the dealtable id // 15.
 * @param {arrayofObj} groups - Contains the JSON of group payload.
 */
export const updateDealTableGroup = (dealTableId, groups) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DEALTABLES}/${dealTableId}/groups`, groups)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

/**
 * "removeDealTableGroup" func removes a particular group from dealtable.
 * @param {number} dealTableId - Refers to the dealtable id // 15.
 * @param {number} groupId - Refers to the group id // 10.
 */
export const removeDealTableGroup = async (dealTableId, groupId) => {
  const groups = await deleteApi(
    `${APIS.DEALTABLES}/${dealTableId}/groups/${groupId}`,
    groupId,
  )
    .then(res => res)
    .catch(error => {
      console.error("DealTableServices => removeDealTableGroup", error);
      throw error;
    });
  return groups;
};

/**
 * "removeAllDealTableGroups" func removes all the group from the dealtable
 * @param {number} dealTableId - Refers to the dealtable id // 15.
 */
export const removeAllDealTableGroups = async dealTableId => {
  const groups = await deleteApi(`${APIS.DEALTABLES}/${dealTableId}/groups`)
    .then(res => res)
    .catch(error => {
      console.error("DealTableServices => removeDealTableGroup", error);
      throw error;
    });
  return groups;
};

/**
 * "updateOverallDealDiscount" func updates the discount applied to a dealtable.
 * @param {number} dealTableId - Refers to the dealtable id // 15.
 * @param {number} discountValue - Refers to the dicount % applied // 10.
 */
export const updateOverallDealDiscount = (dealTableId, discountValue) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DEALTABLES}/${dealTableId}`, discountValue)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const addItemsToDealTable = async (
  dealTableId,
  documentId,
  pricelistId,
  productIds,
  groupedBy,
  dealItems,
  roundingType,
) => {
  try {
    const payload = {
      current_dealitems: dealItems,
      document_id: documentId,
      group_by: groupedBy,
      price_items: productIds,
      price_list_id: pricelistId,
      rounding_flag: roundingType,
    };
    const rowData = await put(
      `${APIS.DEALS}/${dealTableId}/deal_items`,
      payload,
    )
      .then(res => res)
      .catch(error => {
        console.error("DealTableServices => addItemsToDealTable", error);
        throw error;
      });
    return rowData;
  } catch (error) {
    console.error("DealTableServices => addItemsToDealTable", error);
    throw error;
  }
};

export const getDealTableDiscount = async dealTableId => {
  try {
    const discountData = await get(`${APIS.DEALTABLES}/${dealTableId}/groups`)
      .then(res => res)
      .catch(error => {
        console.error("DealTableServices => addItemsToDealTable", error);
        throw error;
      });
    return discountData;
  } catch (error) {
    console.error("DealTableServices => addItemsToDealTable", error);
    throw error;
  }
};



// WEBPACK FOOTER //
// ./src/actions/DealTableActions/DealTableServices.js